/**
 * FloatingVoiceButton - Global voice access
 * 
 * World-class voice UX: always accessible, one tap to start
 * 
 * Features:
 * - Fixed position, always visible
 * - Animated orb with audio-reactive feedback
 * - Haptic feedback on interaction
 * - Respects reduced motion preferences
 * - Hidden during active voice mode (to avoid duplication)
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useVibeMode } from '@/hooks/useVibeMode';
import { haptics } from '@/lib/haptics';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FloatingVoiceButtonProps {
  className?: string;
}

export function FloatingVoiceButton({ className }: FloatingVoiceButtonProps) {
  const { isVibeMode, enterVoiceMode } = useVibeMode();
  const prefersReducedMotion = useReducedMotion();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(() => {
    haptics.medium();
    enterVoiceMode();
  }, [enterVoiceMode]);

  // Hide when already in voice mode
  if (isVibeMode) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ 
              type: 'spring', 
              stiffness: 260, 
              damping: 20,
              delay: 0.5 
            }}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn(
              "fixed bottom-20 right-4 z-50",
              "h-14 w-14 rounded-full",
              "flex items-center justify-center",
              "shadow-2xl",
              "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background",
              "transition-transform active:scale-95",
              className
            )}
            style={{
              background: 'linear-gradient(135deg, hsl(280, 100%, 60%), hsl(250, 100%, 65%), hsl(220, 100%, 70%))',
            }}
            aria-label="Start voice mode"
          >
            {/* Outer glow */}
            <motion.div
              className="absolute inset-[-6px] rounded-full blur-lg"
              animate={{
                opacity: isHovered ? 0.8 : 0.5,
                scale: isHovered ? 1.1 : 1,
              }}
              transition={{ duration: 0.3 }}
              style={{
                background: 'radial-gradient(circle, hsl(250, 100%, 65%) 0%, hsl(280, 100%, 55%) 40%, transparent 70%)',
              }}
            />

            {/* Pulsing ring */}
            {!prefersReducedMotion && (
              <motion.div
                className="absolute inset-0 rounded-full"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  background: 'linear-gradient(135deg, hsl(280, 100%, 60%), hsl(250, 100%, 65%))',
                }}
              />
            )}

            {/* Icon container */}
            <div className="relative z-10 flex items-center justify-center">
              <Mic className="h-6 w-6 text-white" />
            </div>
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-sm">
          <div className="flex items-center gap-2">
            <Mic className="h-3.5 w-3.5" />
            <span>Start Voice Mode</span>
            <kbd className="ml-1 px-1.5 py-0.5 rounded bg-muted text-xs font-mono">V</kbd>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
